import React, { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { CreditCard, TrendUp, UsersThree } from '@phosphor-icons/react';

export default function AdminSubscriptionsPage() {
  const { api } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([api.get('/admin/stats'), api.get('/admin/users')])
      .then(([s, u]) => { setStats(s.data); setUsers(u.data); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [api]);

  if (loading) return <p className="text-sm text-zinc-500">Loading...</p>;

  const subs = stats?.subscriptions || {};
  const totalUsers = (subs.starter || 0) + (subs.pro || 0) + (subs.enterprise || 0);
  const mrr = (subs.starter || 0) * 99 + (subs.pro || 0) * 149 + (subs.enterprise || 0) * 199;
  const arr = mrr * 12;

  const plans = [
    { name: 'Starter', price: 99, count: subs.starter || 0, color: 'bg-zinc-200', textColor: 'text-zinc-700', features: 'Up to 3 properties' },
    { name: 'Pro', price: 149, count: subs.pro || 0, color: 'bg-[#0052FF]', textColor: 'text-[#0052FF]', features: 'Up to 15 properties' },
    { name: 'Scale', price: 199, count: subs.enterprise || 0, color: 'bg-zinc-900', textColor: 'text-zinc-900', features: 'Unlimited properties' },
  ];

  return (
    <div data-testid="admin-subscriptions-page">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-zinc-950 tracking-tight" style={{ fontFamily: 'var(--font-heading)' }}>Subscriptions</h1>
        <p className="text-sm text-zinc-500 mt-1">Revenue metrics and plan distribution</p>
      </div>

      {/* Revenue Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border border-zinc-200 bg-white mb-8" data-testid="revenue-cards">
        <div className="p-6 border-r border-b border-zinc-200" data-testid="mrr-card">
          <div className="w-9 h-9 bg-emerald-50 flex items-center justify-center rounded-sm mb-3">
            <TrendUp size={18} weight="duotone" className="text-emerald-600" />
          </div>
          <p className="text-3xl font-black text-zinc-950 tracking-tight" style={{ fontFamily: 'var(--font-heading)' }}>${mrr.toLocaleString()}</p>
          <p className="text-xs text-zinc-500 font-medium mt-1 uppercase tracking-wide">Monthly Recurring Revenue</p>
        </div>
        <div className="p-6 border-r border-b border-zinc-200" data-testid="arr-card">
          <div className="w-9 h-9 bg-blue-50 flex items-center justify-center rounded-sm mb-3">
            <CreditCard size={18} weight="duotone" className="text-[#0052FF]" />
          </div>
          <p className="text-3xl font-black text-zinc-950 tracking-tight" style={{ fontFamily: 'var(--font-heading)' }}>${arr.toLocaleString()}</p>
          <p className="text-xs text-zinc-500 font-medium mt-1 uppercase tracking-wide">Annual Run Rate</p>
        </div>
        <div className="p-6 border-b border-zinc-200" data-testid="subscribers-card">
          <div className="w-9 h-9 bg-violet-50 flex items-center justify-center rounded-sm mb-3">
            <UsersThree size={18} weight="duotone" className="text-violet-600" />
          </div>
          <p className="text-3xl font-black text-zinc-950 tracking-tight" style={{ fontFamily: 'var(--font-heading)' }}>{totalUsers}</p>
          <p className="text-xs text-zinc-500 font-medium mt-1 uppercase tracking-wide">Total Subscribers</p>
        </div>
      </div>

      {/* Plan Breakdown */}
      <div className="bg-white border border-zinc-200 mb-8" data-testid="plan-breakdown">
        <div className="px-6 py-4 border-b border-zinc-200">
          <h2 className="text-sm font-bold text-zinc-950 uppercase tracking-wide" style={{ fontFamily: 'var(--font-heading)' }}>Plan Distribution</h2>
        </div>
        <div className="divide-y divide-zinc-100">
          {plans.map((plan, i) => (
            <div key={i} className="px-6 py-5 flex items-center justify-between" data-testid={`plan-row-${plan.name.toLowerCase()}`}>
              <div className="flex items-center gap-4">
                <div className={`w-3 h-3 rounded-full ${plan.color}`} />
                <div>
                  <p className="text-sm font-bold text-zinc-900">{plan.name}</p>
                  <p className="text-xs text-zinc-500">{plan.features}</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-sm font-bold text-zinc-900">{plan.count} users</p>
                <p className="text-xs text-zinc-500">${(plan.count * plan.price).toLocaleString()}/mo revenue</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Users by Plan */}
      <div className="bg-white border border-zinc-200" data-testid="users-by-plan">
        <div className="px-6 py-4 border-b border-zinc-200">
          <h2 className="text-sm font-bold text-zinc-950 uppercase tracking-wide" style={{ fontFamily: 'var(--font-heading)' }}>All Subscribers</h2>
        </div>
        {users.length === 0 ? (
          <div className="px-6 py-12 text-center">
            <p className="text-sm text-zinc-500">No subscribers yet.</p>
          </div>
        ) : (
          <div className="divide-y divide-zinc-100">
            {users.map((u, i) => (
              <div key={i} className="px-6 py-3 flex items-center justify-between" data-testid={`subscriber-${i}`}>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-zinc-200 rounded-full flex items-center justify-center text-xs font-bold text-zinc-600">
                    {u.name?.charAt(0)?.toUpperCase() || '?'}
                  </div>
                  <div>
                    <p className="text-sm font-bold text-zinc-900">{u.name}</p>
                    <p className="text-xs text-zinc-500">{u.email} &middot; {u.property_count || 0} properties</p>
                  </div>
                </div>
                <span className={`text-xs px-2 py-0.5 font-bold ${u.subscription_plan === 'pro' ? 'bg-blue-50 text-blue-700' : u.subscription_plan === 'enterprise' ? 'bg-zinc-900 text-white' : 'bg-zinc-100 text-zinc-600'}`}>
                  {u.subscription_plan}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
